import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, useSnackbar } from '@bb-ui/react-library';
import { Fade } from '@material-ui/core';
import { usePrevious } from 'react-use';
import { t } from 'i18next';
import { useLicenseManagementContext } from '../../../../contexts/LicenseManagementContext';
import { User } from '../../../../contexts/LicenseManagementContext/LicenseManagementContext.types';
import { AUTHOR_ROLE_NAME } from '../roles';
import { useAssetCheck } from '../AssetsTransfer/useAssetCheck';
import { useUserDeletion } from './useUserDeletion';
import { useAssetTransfer } from '../AssetsTransfer/useAssetTransfer';
import { DeleteUserWithNoAssetsDialogContent } from './dialogContents/DeleteUserWithNoAssetsDialogContent';
import { useDelayedCall } from '../../../../hooks/useDelayedCall';
import { ApiFailureDialogContent } from '../../../ApiFailureDialogContent/ApiFailureDialogContent';
import { UserDeleteDialogState } from './UserDeleteDialogState';
import { WaitingIfAuthorHasAssetsDialogContent } from '../AssetsTransfer/dialog/WaitingIfAuthorHasAssetsDialogContent';
import { TransferAssetsDialogContent } from '../AssetsTransfer/dialog/TransferAssetsDialogContent';

const DIALOG_STATE_TO_TITLE = {
    [UserDeleteDialogState.DeletionOfUserWithNoAssets]:
        'settings.licenseManagement.delete.title',
    [UserDeleteDialogState.AssetValidation]:
        'settings.licenseManagement.assetCheck.title',
    [UserDeleteDialogState.TransferAssets]:
        'settings.licenseManagement.assetTransfer.title',
    [UserDeleteDialogState.AssetValidationFail]:
        'settings.licenseManagement.assetCheck.errorTitle',
    [UserDeleteDialogState.TransferAssetsFail]:
        'settings.licenseManagement.assetTransfer.errorTitle',
    [UserDeleteDialogState.DeleteFail]:
        'settings.licenseManagement.delete.errorTitle',
};

const DIALOG_STATE_TO_ERROR_TEXT = {
    [UserDeleteDialogState.DeleteFail]:
        'settings.licenseManagement.delete.errorMessage',
    [UserDeleteDialogState.TransferAssetsFail]:
        'settings.licenseManagement.assetTransfer.errorMessage',
    [UserDeleteDialogState.AssetValidationFail]:
        'settings.licenseManagement.assetCheck.errorMessage',
};

export const UserDeleteDialog = (props: {
    userToDelete: User | undefined;
    setUserToDelete: React.Dispatch<React.SetStateAction<User | undefined>>;
    onSuccessfulDeletion?: () => void;
    onClose?: () => void;
}) => {
    const { userToDelete, setUserToDelete, onSuccessfulDeletion, onClose } =
        props;
    const prevUserToDelete = usePrevious(userToDelete);

    const { t } = useTranslation();
    const { deleteUser: deleteUserFromCache } = useLicenseManagementContext();
    const { enqueueSnackbar } = useSnackbar();

    const [dialogStateInternal, setDialogStateInternal] =
        React.useState<UserDeleteDialogState>(UserDeleteDialogState.Closed);

    const openWhenPossible = React.useRef(false);

    // to avoid putting state checks (which is inconsistent) into callbacks
    const currentStateRefReplica = React.useRef(dialogStateInternal);

    // to avoid rendering next dialog frame before transition makes current one fade out
    const nextTransitionState = React.useRef<UserDeleteDialogState>(
        UserDeleteDialogState.Closed,
    );

    // so that when the dialog fades out, we don't render a blank window
    const lastKnownOpenStateRef = React.useRef<
        Exclude<UserDeleteDialogState, UserDeleteDialogState.Closed> | undefined
    >(undefined);

    const [fade, setFade] = React.useState(true);
    const failureCloseButtonRef = React.useRef<HTMLButtonElement>(null);

    const setDialogStateWithRefs = React.useMemo(
        () => (newState: UserDeleteDialogState) => {
            if (newState !== UserDeleteDialogState.Closed) {
                lastKnownOpenStateRef.current = newState;
            }
            setDialogStateInternal(newState);
            currentStateRefReplica.current = newState;
        },
        [],
    );

    const { call: unfadeWithDelay } = useDelayedCall(() => {
        setFade(true);
    });

    const postFadeTransition = React.useMemo(
        () => () => {
            const nextState = nextTransitionState.current;
            if (nextState !== UserDeleteDialogState.Closed) {
                unfadeWithDelay(100);
            }
            setDialogStateWithRefs(nextState);
        },
        [setDialogStateWithRefs, unfadeWithDelay],
    );

    const setDialogState = React.useMemo(
        () => (newState: UserDeleteDialogState) => {
            nextTransitionState.current = newState;
            if (
                currentStateRefReplica.current !==
                    UserDeleteDialogState.Closed &&
                newState !== UserDeleteDialogState.Closed
            ) {
                setFade(false);
            } else {
                setFade(true);
                setDialogStateWithRefs(newState);
            }
        },
        [setDialogStateWithRefs],
    );

    const close = React.useMemo(
        () => () => {
            setDialogState(UserDeleteDialogState.Closed);
        },
        [setDialogState],
    );

    const { checkUserAssets, assetCheckInProgress } = useAssetCheck({
        onAssetsPresent: () => {
            setDialogState(UserDeleteDialogState.TransferAssets);
        },
        onAssetsAbsent: () => {
            setDialogState(UserDeleteDialogState.DeletionOfUserWithNoAssets);
        },
        onAssetCheckError: () => {
            setDialogState(UserDeleteDialogState.AssetValidationFail);
        },
    });

    const { transferAssets, transferInProgress } = useAssetTransfer({
        onTransferSuccess: () => {
            enqueueSnackbar(
                t('settings.licenseManagement.assetTransfer.snackbarSuccess'),
            );
            setDialogState(UserDeleteDialogState.DeletionOfUserWithNoAssets);
        },
        onTransferFailure: () => {
            setDialogState(UserDeleteDialogState.TransferAssetsFail);
        },
    });

    const { deleteUser, deleteInProgress } = useUserDeletion({
        onDeleteSuccess: () => {
            enqueueSnackbar(
                t('settings.licenseManagement.delete.snackbarSuccess'),
            );
            deleteUserFromCache(userToDelete!.userId);
            onSuccessfulDeletion?.();
            close();
        },
        onDeleteFailure: () => {
            setDialogState(UserDeleteDialogState.DeleteFail);
        },
    });

    // init --> either asset check, or direct deletion dialog for non-authors
    React.useEffect(() => {
        if (userToDelete === undefined) {
            close();
            return;
        }

        if (prevUserToDelete !== userToDelete) {
            openWhenPossible.current = true;
        }

        if (!openWhenPossible.current) {
            return;
        }

        if (assetCheckInProgress || deleteInProgress || transferInProgress) {
            // user closed the window while an operation was in progress
            // don't allow them to open a new window until old operation finishes
            return;
        }

        openWhenPossible.current = false;

        const isAuthor = userToDelete?.roles
            ?.map(r => r.name)
            .includes(AUTHOR_ROLE_NAME);

        // this causes 1 frame delay on opening the dialog :>
        setDialogState(
            isAuthor
                ? UserDeleteDialogState.AssetValidation
                : UserDeleteDialogState.DeletionOfUserWithNoAssets,
        );

        // initiate asset check straight away
        if (isAuthor) {
            checkUserAssets(userToDelete);
        }
    }, [
        assetCheckInProgress,
        checkUserAssets,
        close,
        deleteInProgress,
        prevUserToDelete,
        setDialogState,
        transferInProgress,
        userToDelete,
    ]);

    // when closing, show last known open state as the dialog window would otherwise
    // fade out without content and look weird
    const contentState =
        dialogStateInternal !== UserDeleteDialogState.Closed
            ? dialogStateInternal
            : lastKnownOpenStateRef.current;

    // if the dialog is used wrong and the userToDelete changes while it's open,
    // we throw an error to avoid inconsistent states
    if (
        userToDelete !== undefined &&
        prevUserToDelete !== undefined &&
        prevUserToDelete !== userToDelete &&
        dialogStateInternal !== UserDeleteDialogState.Closed
    ) {
        throw new Error(
            'UserDeleteDialog: userToDelete changed while dialog was open. This is not supported to avoid inconsistent states.',
        );
    }

    return (
        // the dialog is pre-rendered to avoid unmounting issues when switching states
        <Dialog
            open={dialogStateInternal !== UserDeleteDialogState.Closed}
            maxWidth="md"
            disableEscapeKeyDown={
                assetCheckInProgress || transferInProgress || deleteInProgress
            }
            TransitionProps={{
                onExited: () => {
                    // Clear parent state after fade-out animation completes
                    setUserToDelete(undefined);
                    onClose?.();
                },
            }}
            aria-labelledby="user-delete-dialog-title"
            data-testid="delete-user-modal"
        >
            <Fade
                in={fade}
                timeout={300}
                onExited={postFadeTransition}
                // The failure content mounts while the fade is hidden, where
                // focus() is ignored, so focus Close once the fade-in is done.
                onEntered={() => failureCloseButtonRef.current?.focus()}
            >
                <div>
                    <DialogTitle
                        id="user-delete-dialog-title"
                        data-testid="delete-user-dialog-title"
                        hideCloseButton={true}
                    >
                        {contentState
                            ? t(DIALOG_STATE_TO_TITLE[contentState])
                            : ''}
                    </DialogTitle>

                    {contentState === UserDeleteDialogState.AssetValidation && (
                        <WaitingIfAuthorHasAssetsDialogContent />
                    )}

                    {contentState === UserDeleteDialogState.TransferAssets && (
                        <TransferAssetsDialogContent
                            userToTransferFrom={userToDelete}
                            transferAssets={transferAssets}
                            transferInProgress={transferInProgress}
                            closeDialog={close}
                            infoText={t(
                                'settings.licenseManagement.assetTransfer.infoMessageDelete',
                            )}
                        />
                    )}

                    {contentState ===
                        UserDeleteDialogState.DeletionOfUserWithNoAssets && (
                        <DeleteUserWithNoAssetsDialogContent
                            userToDelete={userToDelete}
                            deleteUser={deleteUser}
                            deleteInProgress={deleteInProgress}
                            closeDialog={close}
                        />
                    )}

                    {(contentState === UserDeleteDialogState.DeleteFail ||
                        contentState ===
                            UserDeleteDialogState.AssetValidationFail ||
                        contentState ===
                            UserDeleteDialogState.TransferAssetsFail) && (
                        <ApiFailureDialogContent
                            closeDialog={close}
                            closeTestId="delete-user-cancel"
                            closeButtonRef={failureCloseButtonRef}
                            errorText={
                                t(DIALOG_STATE_TO_ERROR_TEXT[contentState]) ||
                                ''
                            }
                        />
                    )}
                </div>
            </Fade>
        </Dialog>
    );
};
